<template>
  
  <report-panel icon="$averageRate" :title="title" :loading="loading">

    <div ref="chartContainer">
      <bar
        :chart-options="chartOptions"
        :chart-data="chartData"
        :styles="chartStyles"
      />
    </div>

  </report-panel>

</template>

<script setup lang="ts">

import { computed, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import ReportPanel from './ReportPanel.vue'
import useFitChartToContainer from '../../../../utils/useFitChartToContainer'
import type { ReportingDateSummaries } from '../../../../services/api-clients/ReportingApi'
import type { BreakdownMode } from '../types'

// ChartJS
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Tooltip,
  LineElement,
  LineController,
  PointElement,
  BarElement,
  CategoryScale,
  LinearScale, type ChartOptions, type TooltipItem, type ChartData,
} from 'chart.js'

ChartJS.register(Tooltip, LineElement, LineController, PointElement, BarElement, CategoryScale, LinearScale)

interface Props {
  dateSummaries: ReportingDateSummaries
  mode: BreakdownMode
  loading: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const { n, t } = useI18n()

const title = computed(() => {
  switch (props?.mode?.name) {
    case 'channel':
      return t('titles.average_rate_by_channel')
      break
    case 'vertical':
      return t('titles.average_rate_by_vertical')
      break
    default:
      return t('misc.unknown_item')
      break
  }
})

/**
 * Chart
 */

const chartOptions: ChartOptions<'bar'> = {
  scales: {
    y: {
      ticks: {
        callback: (value, _index, _values) => {
          return n(value as number, {style: 'currency', currency: 'USD', maximumFractionDigits: 0})
        },
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'bar'>) => {
          return n(context.raw as number, 'currency')
        },
      },
    },
  },
}

const chartData = computed<ChartData<'bar', number[]>>(() => {

  const labels: string[] = []
  const categoriesToPlot: string[] = []
  const colors: string[] = []

  // build categoriesToPlot

  Object.values(props.dateSummaries).forEach((summary) => {
    
    const source = summary.as_transaction_date.gross_revenue[props.mode.dataKey]
    
    Object.keys(source).forEach((category) => {
      if (!categoriesToPlot.includes(category)) {
        categoriesToPlot.push(category)
        labels.push(props.mode.labelsEnum[category] ?? category) // TODO: i18n?
        colors.push(props.mode.colorsEnum[category] ?? '#787878')
      }
    })

  })

  interface CategoryTotals {
    rates: number
    transactions: number
  }

  // scaffold a temp object that we'll use to collect per-category totals
  const totalsByCategory: {[key: string]: CategoryTotals} = categoriesToPlot.reduce((accumulator, category) => {
    accumulator[category] = {rates: 0, transactions: 0}
    return accumulator
  }, {} as {[key: string]: CategoryTotals})

  // now calculate those per-category totals
  Object.values(props.dateSummaries).forEach((summary) => {

    const rateSource = summary.as_transaction_date.gross_revenue[props.mode.dataKey]
    const transactionSource = summary.as_transaction_date.transactions[props.mode.dataKey]
    
    categoriesToPlot.forEach((category) => {      
      totalsByCategory[category].rates += rateSource[category] ?? 0
      totalsByCategory[category].transactions += transactionSource[category] ?? 0
    })

  })

  // finally, divide to get the averages
  const data = categoriesToPlot.map((category) => {
    return totalsByCategory[category].rates / totalsByCategory[category].transactions
  })

  return {
    labels: labels,
    datasets: [
      {
        backgroundColor: colors,
        data: data,
      },
    ],
  }

})

/**
 * Adjust the chart size responsively
 * https://vue-chartjs.org/guide/#chart-with-dynamic-styles
 */

const chartContainer = useTemplateRef('chartContainer') // template ref
const { chartStyles } = useFitChartToContainer(chartContainer)

</script>
7